/**
 * AG-UI Chat Slice Factory
 *
 * Factory function to create Redux slices for AG-UI chat features.
 * Provides common reducers for message handling, streaming, and interrupts.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  ActivityType,
  AssistantStatus,
  BaseAgUiChatState,
  ChoiceOption,
  ConfirmationNamespace,
  ConfirmationType,
  EditPreview,
  EntityUpdateState,
  InterruptEventType,
  InterruptState,
  SSEPerformanceMetrics,
  StepPayload,
} from './types';

// ============================================================================
// Factory Configuration
// ============================================================================

export interface SliceFactoryConfig {
  /**
   * Name prefix for the slice (e.g., 'noteAiEdit', 'rulesAiEdit')
   */
  name: string;

  /**
   * Entity type for namespace matching (e.g., 'section', 'rule')
   */
  entityType: string;

  /**
   * Additional initial state fields specific to the feature
   */
  additionalState?: Record<string, unknown>;
}

// ============================================================================
// Combined State Type
// ============================================================================

export type AgUiChatState = BaseAgUiChatState & EntityUpdateState;

// ============================================================================
// Initial State Factory
// ============================================================================

export const createInitialState = (
  additionalState?: Record<string, unknown>,
): AgUiChatState & Record<string, unknown> => ({
  // Session
  threadId: null,

  // Messages
  messages: [],

  // Activity status messages
  activities: {},

  // Streaming state
  assistant: {
    status: 'idle',
    currentStep: null,
    streamingContent: '',
    currentMessageId: null,
    firstTokenReceived: false,
  },

  // Entity streaming updates
  entityUpdates: {},
  streamingEntities: [],
  entityPreviousContent: {},
  affectedEntityIds: [],

  // Unified interrupt state
  interrupt: null,

  // UI state
  isChatVisible: true,

  // Error handling
  error: null,

  // SSE performance metrics
  ssePerformanceMetrics: null,

  // Spread any additional state
  ...additionalState,
});

// ============================================================================
// Slice Factory
// ============================================================================

/**
 * Creates a Redux slice for AG-UI chat functionality.
 *
 * @param config - Configuration for the slice
 * @returns Redux slice with common chat reducers
 */
export const createAgUiChatSlice = <TAdditionalState extends Record<string, unknown> = Record<string, never>>(
  config: SliceFactoryConfig,
) => {
  const { name, additionalState } = config;

  type StateType = AgUiChatState & TAdditionalState;

  const initialState = createInitialState(additionalState) as StateType;

  return createSlice({
    name: `${name}/ui`,
    initialState,
    reducers: {
      // ========================================================================
      // Session Management
      // ========================================================================

      initializeChat: (state, action: PayloadAction<{ entityId: string }>) => {
        state.threadId = null;
        state.messages = [];
        state.error = null;
        state.assistant.status = 'idle';
        state.entityUpdates = {};
        state.streamingEntities = [];
        state.entityPreviousContent = {};
        state.affectedEntityIds = [];
      },

      setThreadId: (state, action: PayloadAction<string>) => {
        state.threadId = action.payload;
      },

      // ========================================================================
      // Message Management
      // ========================================================================

      addUserMessage: (state, action: PayloadAction<string>) => {
        const now = new Date();
        state.messages.push({
          id: `user-${now.getTime()}`,
          role: 'user',
          content: action.payload,
          timestamp: now.toISOString(),
        });
      },

      // ========================================================================
      // Streaming State
      // ========================================================================

      setAssistantStatus: (state, action: PayloadAction<AssistantStatus>) => {
        state.assistant.status = action.payload;
      },

      setCurrentStep: (state, action: PayloadAction<string>) => {
        state.assistant.currentStep = action.payload;
      },

      startStreamingMessage: (state, action: PayloadAction<string>) => {
        state.assistant.currentMessageId = action.payload;
        state.assistant.streamingContent = '';
        state.assistant.firstTokenReceived = false;
        state.assistant.status = 'streaming';
      },

      appendStreamingContent: (state, action: PayloadAction<string>) => {
        state.assistant.streamingContent += action.payload;
        if (!state.assistant.firstTokenReceived) {
          state.assistant.firstTokenReceived = true;
        }
      },

      finalizeMessage: state => {
        if (state.assistant.streamingContent) {
          const now = new Date();
          state.messages.push({
            id: state.assistant.currentMessageId || `assistant-${now.getTime()}`,
            role: 'assistant',
            content: state.assistant.streamingContent,
            timestamp: now.toISOString(),
          });
        }
        state.assistant.streamingContent = '';
        state.assistant.currentMessageId = null;
        state.assistant.firstTokenReceived = false;
        state.assistant.status = 'thinking';
      },

      // ========================================================================
      // Activity Management
      // ========================================================================

      setActivitySnapshot: (
        state,
        action: PayloadAction<{
          messageId: string;
          activityType: ActivityType;
          message: string;
          timestamp: number;
          replace?: boolean;
        }>,
      ) => {
        const { messageId, activityType, message, timestamp, replace = true } = action.payload;

        if (!replace && state.activities[messageId]) {
          return;
        }

        state.activities[messageId] = {
          messageId,
          activityType,
          message,
          timestamp,
        };
      },

      clearActivity: (state, action: PayloadAction<string>) => {
        const messageId = action.payload;
        const { [messageId]: _removed, ...remaining } = state.activities;
        state.activities = remaining;
      },

      clearAllActivities: state => {
        state.activities = {};
      },

      // ========================================================================
      // Entity Streaming
      // ========================================================================

      startEntityStreaming: (state, action: PayloadAction<string>) => {
        const entityId = action.payload;
        state.entityUpdates[entityId] = '';
        if (!state.streamingEntities.includes(entityId)) {
          state.streamingEntities.push(entityId);
        }
      },

      appendEntityContent: (
        state,
        action: PayloadAction<{ entityId: string; content: string }>,
      ) => {
        const { entityId, content } = action.payload;
        if (state.entityUpdates[entityId] !== undefined) {
          state.entityUpdates[entityId] += content;
        } else {
          state.entityUpdates[entityId] = content;
        }
      },

      finalizeEntityUpdate: (state, action: PayloadAction<string>) => {
        const entityId = action.payload;
        const { [entityId]: _removed, ...remaining } = state.entityUpdates;
        state.entityUpdates = remaining;
      },

      finalizeEntityStreaming: (state, action: PayloadAction<string>) => {
        const entityId = action.payload;
        state.streamingEntities = state.streamingEntities.filter(id => id !== entityId);
      },

      clearAllEntityUpdates: state => {
        state.entityUpdates = {};
        state.streamingEntities = [];
      },

      // ========================================================================
      // Entity Snapshot and Affected State Management
      // ========================================================================

      saveEntitySnapshot: (
        state,
        action: PayloadAction<{ entityId: string; content: string }>,
      ) => {
        const { entityId, content } = action.payload;
        state.entityPreviousContent[entityId] = content;
      },

      markEntityAffected: (state, action: PayloadAction<string>) => {
        const entityId = action.payload;
        if (!state.affectedEntityIds.includes(entityId)) {
          state.affectedEntityIds.push(entityId);
        }
      },

      clearAffectedEntity: (state, action: PayloadAction<string>) => {
        const entityId = action.payload;
        state.affectedEntityIds = state.affectedEntityIds.filter(id => id !== entityId);
      },

      clearEntitySnapshot: (state, action: PayloadAction<string>) => {
        const entityId = action.payload;
        const { [entityId]: _removed, ...remaining } = state.entityPreviousContent;
        state.entityPreviousContent = remaining;
      },

      // ========================================================================
      // Interrupt Management
      // ========================================================================

      showInterrupt: (
        state,
        action: PayloadAction<{
          interruptId: string;
          eventType: InterruptEventType;
          // CONFIRMATION fields
          confirmLabel?: string;
          cancelLabel?: string;
          message?: string;
          edits?: EditPreview[];
          namespace?: ConfirmationNamespace;
          confirmationType?: ConfirmationType;
          // MULTIPLE_CHOICE fields
          question?: string;
          options?: ChoiceOption[];
          allowMultiple?: boolean;
          allowSelectAll?: boolean;
          minSelections?: number;
          maxSelections?: number;
          allowTextInput?: boolean;
          textInputPlaceholder?: string;
          // MULTI_STEP fields
          steps?: StepPayload[];
          title?: string;
          // EXPRESS_INTEREST fields
          detectedIntent?: string;
          originalMessage?: string;
          metadata?: unknown | null;
        }>,
      ) => {
        const { interruptId, eventType, ...fields } = action.payload;

        state.interrupt = {
          isActive: true,
          interruptId,
          eventType,
          selectedOptionIds: [],
          currentStepIndex: eventType === 'MULTI_STEP' ? 0 : undefined,
          stepResponses: eventType === 'MULTI_STEP' ? {} : undefined,
          ...fields,
        };

        // Set appropriate assistant status based on interrupt type
        if (eventType === 'CONFIRMATION') {
          state.assistant.status = 'awaiting_confirmation';
        } else {
          state.assistant.status = 'awaiting_selection';
        }
      },

      toggleOptionSelection: (state, action: PayloadAction<string>) => {
        if (!state.interrupt) return;

        const optionId = action.payload;
        const selectedIds = state.interrupt.selectedOptionIds;

        if (state.interrupt.eventType === 'CONFIRMATION') {
          state.interrupt.selectedOptionIds = [optionId];
          return;
        }

        if (selectedIds.includes(optionId)) {
          state.interrupt.selectedOptionIds = selectedIds.filter(id => id !== optionId);
        } else {
          state.interrupt.selectedOptionIds.push(optionId);
        }
      },

      selectAllOptions: state => {
        if (!state.interrupt || state.interrupt.eventType !== 'MULTIPLE_CHOICE') return;
        if (!state.interrupt.options) return;

        state.interrupt.selectedOptionIds = state.interrupt.options.map(opt => opt.id);
      },

      deselectAllOptions: state => {
        if (!state.interrupt || state.interrupt.eventType !== 'MULTIPLE_CHOICE') return;

        state.interrupt.selectedOptionIds = [];
      },

      setStepResponse: (
        state,
        action: PayloadAction<{
          stepId: string;
          selectedIds?: string[];
          confirmed?: boolean;
        }>,
      ) => {
        if (!state.interrupt || state.interrupt.eventType !== 'MULTI_STEP') return;

        const { stepId, selectedIds, confirmed } = action.payload;
        if (!state.interrupt.stepResponses) {
          state.interrupt.stepResponses = {};
        }

        state.interrupt.stepResponses[stepId] = {
          stepId,
          selectedIds,
          confirmed,
        };
      },

      setCurrentStepIndex: (state, action: PayloadAction<number>) => {
        if (!state.interrupt || state.interrupt.eventType !== 'MULTI_STEP') return;

        state.interrupt.currentStepIndex = action.payload;
      },

      clearInterrupt: state => {
        state.interrupt = null;
      },

      // ========================================================================
      // UI State Management
      // ========================================================================

      setChatVisibility: (state, action: PayloadAction<boolean>) => {
        state.isChatVisible = action.payload;
      },

      toggleChatVisibility: state => {
        state.isChatVisible = !state.isChatVisible;
      },

      // ========================================================================
      // Workflow Completion
      // ========================================================================

      completeWorkflow: state => {
        state.assistant.status = 'idle';
        state.assistant.currentStep = null;
        state.interrupt = null;
        state.streamingEntities = [];
      },

      // ========================================================================
      // Error Handling
      // ========================================================================

      setError: (state, action: PayloadAction<string>) => {
        state.error = action.payload;
        state.assistant.status = 'idle';
        state.entityUpdates = {};
        state.streamingEntities = [];
      },

      clearError: state => {
        state.error = null;
      },

      // ========================================================================
      // SSE Performance Tracking
      // ========================================================================

      setSSEPerformanceMetrics: (state, action: PayloadAction<SSEPerformanceMetrics>) => {
        state.ssePerformanceMetrics = action.payload;
      },

      clearSSEPerformanceMetrics: state => {
        state.ssePerformanceMetrics = null;
      },

      // ========================================================================
      // Reset
      // ========================================================================

      resetChat: () => initialState,
    },
  });
};

// ============================================================================
// Type Exports for Slice Usage
// ============================================================================

/**
 * Type helper to get actions from a created slice
 */
export type AgUiChatSliceActions = ReturnType<typeof createAgUiChatSlice>['actions'];

/**
 * Common action types for AG-UI chat slices
 */
export type CommonAgUiChatActions = {
  initializeChat: (payload: { entityId: string }) => PayloadAction<{ entityId: string }>;
  setThreadId: (payload: string) => PayloadAction<string>;
  addUserMessage: (payload: string) => PayloadAction<string>;
  setAssistantStatus: (payload: AssistantStatus) => PayloadAction<AssistantStatus>;
  setCurrentStep: (payload: string) => PayloadAction<string>;
  startStreamingMessage: (payload: string) => PayloadAction<string>;
  appendStreamingContent: (payload: string) => PayloadAction<string>;
  finalizeMessage: () => PayloadAction;
  setActivitySnapshot: (payload: {
    messageId: string;
    activityType: ActivityType;
    message: string;
    timestamp: number;
    replace?: boolean;
  }) => PayloadAction<{
    messageId: string;
    activityType: ActivityType;
    message: string;
    timestamp: number;
    replace?: boolean;
  }>;
  clearActivity: (payload: string) => PayloadAction<string>;
  clearAllActivities: () => PayloadAction;
  startEntityStreaming: (payload: string) => PayloadAction<string>;
  appendEntityContent: (payload: {
    entityId: string;
    content: string;
  }) => PayloadAction<{ entityId: string; content: string }>;
  finalizeEntityUpdate: (payload: string) => PayloadAction<string>;
  finalizeEntityStreaming: (payload: string) => PayloadAction<string>;
  clearAllEntityUpdates: () => PayloadAction;
  saveEntitySnapshot: (payload: {
    entityId: string;
    content: string;
  }) => PayloadAction<{ entityId: string; content: string }>;
  markEntityAffected: (payload: string) => PayloadAction<string>;
  clearAffectedEntity: (payload: string) => PayloadAction<string>;
  clearEntitySnapshot: (payload: string) => PayloadAction<string>;
  showInterrupt: (payload: Partial<InterruptState> & {
    interruptId: string;
    eventType: InterruptEventType;
  }) => PayloadAction<Partial<InterruptState> & {
    interruptId: string;
    eventType: InterruptEventType;
  }>;
  toggleOptionSelection: (payload: string) => PayloadAction<string>;
  selectAllOptions: () => PayloadAction;
  deselectAllOptions: () => PayloadAction;
  setStepResponse: (payload: {
    stepId: string;
    selectedIds?: string[];
    confirmed?: boolean;
  }) => PayloadAction<{
    stepId: string;
    selectedIds?: string[];
    confirmed?: boolean;
  }>;
  setCurrentStepIndex: (payload: number) => PayloadAction<number>;
  clearInterrupt: () => PayloadAction;
  setChatVisibility: (payload: boolean) => PayloadAction<boolean>;
  toggleChatVisibility: () => PayloadAction;
  completeWorkflow: () => PayloadAction;
  setError: (payload: string) => PayloadAction<string>;
  clearError: () => PayloadAction;
  setSSEPerformanceMetrics: (payload: SSEPerformanceMetrics) => PayloadAction<SSEPerformanceMetrics>;
  clearSSEPerformanceMetrics: () => PayloadAction;
  resetChat: () => PayloadAction;
};

